import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Trash2, UserPlus, KeyRound, ShieldCheck, Eye, EyeOff, Link2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const isSC = currentUser?.role === "super_custodian";
  const isHoD = currentUser?.role === "hod";
  const [users, setUsers] = useState([]);
  const [tls, setTls] = useState([]);
  const [hods, setHods] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [tlEmail, setTlEmail] = useState("");
  const [hodEmail, setHodEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [revealPasswords, setRevealPasswords] = useState({});  // { userId: bool }
  const [mapUser, setMapUser] = useState(null);
  const [mapTarget, setMapTarget] = useState("");

  const load = async () => {
    const reqs = [api.get("/users"), api.get("/users/tls")];
    if (isSC) reqs.push(api.get("/users/hods"));
    const [u, t, h] = await Promise.all(reqs);
    setUsers(u.data);
    setTls(t.data);
    setHods(h ? h.data : []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isSC]);

  const add = async (e) => {
    e.preventDefault();
    if (!role) { toast.error("Please select a role"); return; }
    if (role === "recruiter" && !tlEmail) { toast.error("Please select a TL for this recruiter"); return; }
    if (role === "tl" && isSC && hods.length > 0 && !hodEmail) {
      toast.error("Please map this TL to a Head of Delivery");
      return;
    }
    setBusy(true);
    try {
      const payload = { email, role, name };
      if (role === "recruiter") payload.tl_email = tlEmail;
      if (role === "tl" && hodEmail) payload.hod_email = hodEmail;
      await api.post("/users", payload);
      toast.success("User added to approved list");
      setEmail(""); setName(""); setRole(""); setTlEmail(""); setHodEmail("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this user from the approved list?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setPassword = async () => {
    if (!pwUser || !pw) return;
    try {
      await api.post("/users/set-password", { email: pwUser.email, password: pw, name: pwUser.name });
      toast.success("Password set");
      setPwUser(null); setPw(""); setShowPw(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remap = async () => {
    if (!mapUser || !mapTarget) return;
    try {
      const body = mapUser.role === "tl" ? { hod_email: mapTarget } : { tl_email: mapTarget };
      await api.patch(`/users/${mapUser.id}/mapping`, body);
      toast.success("Mapping updated");
      setMapUser(null); setMapTarget("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const superCount = users.filter((u) => u.role === "super_custodian").length;

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-slate-900">User management</h1>
        <p className="text-slate-500 mt-1">
          {isHoD
            ? "Manage TLs and Recruiters under your delivery org."
            : "Approved emails can log in. Manage roles and reset passwords."}
        </p>
      </div>

      <Card className="p-6 border-slate-200 mb-6">
        <h3 className="font-display font-semibold text-lg text-slate-900 mb-1 flex items-center gap-2"><UserPlus className="w-5 h-5 text-indigo-600" />Add approved email</h3>
        <p className="text-xs text-slate-500 mb-4">
          {isHoD
            ? "Add a TL (mapped to you) or a Recruiter (mapped to one of your TLs)."
            : "Step 1: enter email. Step 2: select role. Step 3: map to HoD/TL as required."}
        </p>
        <form onSubmit={add} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Email <span className="text-red-500">*</span></Label>
              <Input data-testid="add-user-email" placeholder="user@getwork.org" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Full name</Label>
              <Input data-testid="add-user-name" placeholder="Optional" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </div>
          </div>

          {email && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 animate-fade-up">
              <div>
                <Label className="text-xs text-slate-600">Role <span className="text-red-500">*</span></Label>
                <Select value={role} onValueChange={(v) => { setRole(v); setTlEmail(""); setHodEmail(""); }}>
                  <SelectTrigger data-testid="add-user-role" className="mt-1"><SelectValue placeholder="Select a role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recruiter">Recruiter</SelectItem>
                    <SelectItem value="tl">Team Lead</SelectItem>
                    {isSC && <SelectItem value="hod">Head of Delivery</SelectItem>}
                    {isSC && (
                      <SelectItem value="super_custodian" disabled={superCount >= 3}>
                        Super Custodian {superCount >= 3 && "(max reached)"}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {role === "tl" && isSC && hods.length > 0 && (
                <div className="animate-fade-up">
                  <Label className="text-xs text-slate-600">Map to Head of Delivery <span className="text-red-500">*</span></Label>
                  <Select value={hodEmail} onValueChange={setHodEmail}>
                    <SelectTrigger data-testid="add-user-hod" className="mt-1"><SelectValue placeholder="Choose an HoD" /></SelectTrigger>
                    <SelectContent>
                      {hods.map((h) => <SelectItem key={h.email} value={h.email}>{h.name || h.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {role === "tl" && isHoD && (
                <div className="animate-fade-up flex items-end">
                  <p className="text-xs text-slate-500 pb-2">This TL will be mapped under you (<span className="font-medium text-slate-700">{currentUser?.email}</span>).</p>
                </div>
              )}

              {role === "recruiter" && (
                <div className="animate-fade-up">
                  <Label className="text-xs text-slate-600">Map to TL <span className="text-red-500">*</span></Label>
                  <Select value={tlEmail} onValueChange={setTlEmail}>
                    <SelectTrigger data-testid="add-user-tl" className="mt-1"><SelectValue placeholder={tls.length === 0 ? "No TLs yet — add a TL first" : "Choose a TL"} /></SelectTrigger>
                    <SelectContent>
                      {tls.map((t) => <SelectItem key={t.email} value={t.email}>{t.name || t.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              data-testid="add-user-submit"
              type="submit"
              disabled={
                busy || !email || !role
                || (role === "recruiter" && !tlEmail)
                || (role === "tl" && isSC && hods.length > 0 && !hodEmail)
              }
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Add user
            </Button>
          </div>
        </form>
        {isSC && superCount >= 3 && <p className="text-xs text-amber-600 mt-3">Maximum Super Custodians reached (3).</p>}
      </Card>

      <Card className="p-0 border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900">{isHoD ? "My team" : "All users"} ({users.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left py-3 px-6 font-semibold">Email</th>
                <th className="text-left py-3 px-6 font-semibold">Name</th>
                <th className="text-left py-3 px-6 font-semibold">Role</th>
                <th className="text-left py-3 px-6 font-semibold">Mapped to</th>
                {isSC && <th className="text-left py-3 px-6 font-semibold">Password</th>}
                <th className="text-left py-3 px-6 font-semibold">Status</th>
                <th className="text-right py-3 px-6 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="py-3 px-6 font-medium text-slate-900">{u.email}</td>
                  <td className="py-3 px-6 text-slate-600">{u.name || "—"}</td>
                  <td className="py-3 px-6">
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${
                      u.role === "super_custodian" ? "bg-indigo-100 text-indigo-700"
                      : u.role === "hod" ? "bg-fuchsia-100 text-fuchsia-700"
                      : u.role === "tl" ? "bg-violet-100 text-violet-700"
                      : "bg-slate-100 text-slate-700"}`}>
                      {u.role === "super_custodian" ? "Super Custodian"
                        : u.role === "hod" ? "HoD"
                        : u.role === "tl" ? "TL" : "Recruiter"}
                    </span>
                  </td>
                  <td className="py-3 px-6 text-slate-600 text-xs">
                    {u.role === "recruiter" && (u.tl_email ? `TL: ${u.tl_email}` : "—")}
                    {u.role === "tl" && (u.hod_email ? `HoD: ${u.hod_email}` : "—")}
                    {(u.role === "hod" || u.role === "super_custodian") && "—"}
                  </td>
                  {isSC && (
                    <td className="py-3 px-6 text-slate-600 text-xs" data-testid={`password-cell-${u.email}`}>
                      {u.password_plaintext ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{revealPasswords[u.id] ? u.password_plaintext : "•".repeat(Math.min(u.password_plaintext.length, 10))}</span>
                          <button
                            data-testid={`toggle-pw-${u.email}`}
                            onClick={() => setRevealPasswords((prev) => ({ ...prev, [u.id]: !prev[u.id] }))}
                            className="text-slate-400 hover:text-slate-700"
                          >
                            {revealPasswords[u.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      ) : <span className="text-slate-300">not set</span>}
                    </td>
                  )}
                  <td className="py-3 px-6">
                    {u.active ? (
                      <span className="text-xs text-emerald-700 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Active</span>
                    ) : (
                      <span className="text-xs text-slate-400">Not registered</span>
                    )}
                  </td>
                  <td className="py-3 px-6 text-right">
                    {(u.email !== currentUser?.email) && (isSC || (isHoD && (u.role === "tl" || u.role === "recruiter"))) && (
                      <>
                        {(u.role === "tl" || u.role === "recruiter") && (
                          <button
                            data-testid={`map-${u.email}`}
                            onClick={() => { setMapUser(u); setMapTarget(u.role === "tl" ? (u.hod_email || "") : (u.tl_email || "")); }}
                            className="text-slate-500 hover:text-indigo-700 mr-3 inline-flex items-center gap-1 text-xs"
                          >
                            <Link2 className="w-3.5 h-3.5" />Map
                          </button>
                        )}
                        <button data-testid={`set-pw-${u.email}`} onClick={() => setPwUser(u)} className="text-slate-500 hover:text-indigo-700 mr-3 inline-flex items-center gap-1 text-xs">
                          <KeyRound className="w-3.5 h-3.5" />Set password
                        </button>
                        <button data-testid={`delete-${u.email}`} onClick={() => remove(u.id)} className="text-slate-500 hover:text-red-700 inline-flex items-center gap-1 text-xs">
                          <Trash2 className="w-3.5 h-3.5" />Remove
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!o) { setPwUser(null); setShowPw(false); setPw(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set password for {pwUser?.email}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label>New password</Label>
            <div className="relative mt-1">
              <Input
                data-testid="set-pw-input"
                type={showPw ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="pr-10"
                placeholder="Min 6 characters"
              />
              <button
                type="button"
                data-testid="toggle-set-pw"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwUser(null); setShowPw(false); setPw(""); }}>Cancel</Button>
            <Button data-testid="set-pw-confirm" onClick={setPassword} className="bg-indigo-600 hover:bg-indigo-700">Set password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remap TL → HoD or Recruiter → TL */}
      <Dialog open={!!mapUser} onOpenChange={(o) => !o && setMapUser(null)}>
        <DialogContent data-testid="map-dialog">
          <DialogHeader><DialogTitle>Map {mapUser?.role === "tl" ? "TL" : "Recruiter"} — {mapUser?.email}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label>{mapUser?.role === "tl" ? "Head of Delivery" : "Team Lead"}</Label>
            <Select value={mapTarget} onValueChange={setMapTarget}>
              <SelectTrigger data-testid="map-target-select" className="mt-1"><SelectValue placeholder={`Choose a ${mapUser?.role === "tl" ? "HoD" : "TL"}`} /></SelectTrigger>
              <SelectContent>
                {(mapUser?.role === "tl" ? hods : tls).map((x) => <SelectItem key={x.email} value={x.email}>{x.name || x.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMapUser(null)}>Cancel</Button>
            <Button data-testid="map-confirm" onClick={remap} className="bg-indigo-600 hover:bg-indigo-700" disabled={!mapTarget}>Update mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
